import { GoogleGenAI, Type } from "@google/genai";
import { LineItem, Order, AISummary, Project, BitumenSpec, QuoteState } from "../types";

// Added comment above fix: Guidelines require creating a new instance right before making an API call to ensure latest API key usage
const getGenAI = () => {
  return new GoogleGenAI({ apiKey: process.env.API_KEY });
};

export interface AIProjectReport {
  summary: string;
  suggestedSpecs: Array<{ grade: string, quantity: number, reasoning: string }>;
  risks: string[];
}

export const generateQuoteItems = async (
  prompt: string,
  currency: string
): Promise<{ 
  items: LineItem[], 
  bitumenSpecs?: BitumenSpec[], 
  clientName?: string, 
  clientAddress?: string, 
  paymentTerms?: string 
}> => {
  try {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Generate a quotation from this prompt: "${prompt}". Currency: ${currency}.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            clientName: { type: Type.STRING },
            clientAddress: { type: Type.STRING },
            paymentTerms: { type: Type.STRING },
            items: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  name: { type: Type.STRING },
                  description: { type: Type.STRING },
                  quantity: { type: Type.NUMBER },
                  unit: { type: Type.STRING },
                  price: { type: Type.NUMBER },
                },
                required: ["name", "description", "quantity", "price"],
              },
            },
            bitumenSpecs: {
                type: Type.ARRAY,
                items: {
                    type: Type.OBJECT,
                    properties: {
                        grade: { type: Type.STRING },
                        quantity: { type: Type.NUMBER }
                    }
                }
            }
          },
        },
      },
    });

    const data = JSON.parse(response.text || '{}');
    return {
      items: (data.items || []).map((it: any) => ({ ...it, id: crypto.randomUUID() })),
      bitumenSpecs: (data.bitumenSpecs || []).map((it: any) => ({ ...it, id: crypto.randomUUID() })),
      clientName: data.clientName,
      clientAddress: data.clientAddress,
      paymentTerms: data.paymentTerms
    };
  } catch (error) {
    console.error(error);
    return { items: [] };
  }
};

export const processDocument = async (base64: string, mimeType: string): Promise<any> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: {
            parts: [
                { inlineData: { mimeType, data: base64 } },
                { text: "Extract all key data points from this document as JSON." }
            ]
        },
        config: { responseMimeType: "application/json" }
    });
    return JSON.parse(response.text || '{}');
};

export const getMarketInsights = async (prompt: string): Promise<{ text: string, sources: any[] }> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: prompt,
        config: { tools: [{ googleSearch: {} }] }
    });
    const sources = response.candidates?.[0]?.groundingMetadata?.groundingChunks?.map((c: any) => ({
        title: c.web?.title || 'Source',
        uri: c.web?.uri || '#'
    })) || [];
    return { text: response.text || '', sources };
};

export const analyzeOrder = async (order: Order): Promise<AISummary> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Analyze this logistics order for risks: ${JSON.stringify(order)}`,
        config: {
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    riskLevel: { type: Type.STRING, enum: ["LOW", "MEDIUM", "HIGH"] },
                    summary: { type: Type.STRING },
                    recommendations: { type: Type.ARRAY, items: { type: Type.STRING } }
                },
                required: ["riskLevel", "summary", "recommendations"]
            }
        }
    });
    return JSON.parse(response.text || '{}') as AISummary;
};

export const generateProjectAnalysis = async (project: Project): Promise<AIProjectReport> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Analyze this construction project: ${JSON.stringify(project)}`,
        config: {
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    summary: { type: Type.STRING },
                    suggestedSpecs: {
                        type: Type.ARRAY,
                        items: {
                            type: Type.OBJECT,
                            properties: {
                                grade: { type: Type.STRING },
                                quantity: { type: Type.NUMBER },
                                reasoning: { type: Type.STRING }
                            }
                        }
                    },
                    risks: { type: Type.ARRAY, items: { type: Type.STRING } }
                }
            }
        }
    });
    return JSON.parse(response.text || '{}') as AIProjectReport;
};

export const generateSmartUpdate = async (project: Project, rawText: string): Promise<string> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Refine this site update text for professional ERP logging. Project: ${project.name}. Raw update: "${rawText}"`,
    });
    return response.text || rawText;
};

export const extractProjectFromText = async (text: string): Promise<Partial<Project>> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Extract project details from this text: "${text}"`,
        config: {
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING },
                    contractor: { type: Type.STRING },
                    region: { type: Type.STRING },
                    kilometers: { type: Type.NUMBER },
                    financer: { type: Type.STRING },
                    consultant: { type: Type.STRING },
                    progress: { type: Type.NUMBER },
                    bitumenSpecs: {
                        type: Type.ARRAY,
                        items: {
                            type: Type.OBJECT,
                            properties: {
                                grade: { type: Type.STRING },
                                quantity: { type: Type.NUMBER }
                            }
                        }
                    }
                }
            }
        }
    });
    return JSON.parse(response.text || '{}');
};

export const analyzeData = async (summary: string): Promise<string> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Provide 3 strategic insights for this port import data: ${summary}`,
    });
    return response.text || '';
};

export const parseManifest = async (input: string | { mimeType: string, data: string }): Promise<any> => {
    const ai = getGenAI();
    const filterInstruction = "CRITICAL: Identify and extract ONLY manifest entries that are explicitly for 'Bitumen' (including common variants like 'BITUMEN GRADE', 'EMULSION', etc.). Any entries for cargo NOT containing the word 'Bitumen' must be strictly ignored and omitted from the results. Ensure 'notifyParty' and 'finalDestination' (if mentioned, e.g. Tanzania, DRC, Zambia, Malawi, Rwanda, Burundi) are extracted if present in the document.";
    
    const parts = typeof input === 'string' 
        ? [{ text: `Parse this manifest data. ${filterInstruction} Extract Importer, Supplier, Final Destination, and Notify Party: ${input}` }]
        : [{ inlineData: input }, { text: `Extract manifest details as JSON. ${filterInstruction} Ensure 'notifyParty' and 'finalDestination' are extracted if present in the document.` }];
    
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: { parts },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            vesselName: { type: Type.STRING },
            imo: { type: Type.STRING },
            imports: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  importerName: { type: Type.STRING },
                  supplier: { type: Type.STRING },
                  notifyParty: { type: Type.STRING, description: "The person or entity to be notified of the arrival of the goods" },
                  finalDestination: { type: Type.STRING, description: "The ultimate destination of the cargo (e.g. Tanzania, DRC, Zambia, Malawi, Rwanda, Burundi)" },
                  quantityMetricTons: { type: Type.NUMBER },
                  grade: { type: Type.STRING },
                  blNumber: { type: Type.STRING }
                }
              }
            }
          }
        }
      }
    });
    return JSON.parse(response.text || '{}');
};

export const processQuoteAssistantMessage = async (message: string, quote: QuoteState): Promise<{ text: string, actions?: any[] }> => {
    const ai = getGenAI();
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `User says: "${message}". Current quote state: ${JSON.stringify(quote)}. Respond with helpful text and structured actions to update the quote.`,
        config: {
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    text: { type: Type.STRING },
                    actions: {
                        type: Type.ARRAY,
                        items: {
                            type: Type.OBJECT,
                            properties: {
                                name: { type: Type.STRING, enum: ["add_line_items", "update_client_info", "configure_pricing"] },
                                args: { type: Type.OBJECT }
                            }
                        }
                    }
                }
            }
        }
    });
    return JSON.parse(response.text || '{"text": "Error processing request"}');
};
